import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "@tsp-core/store/utils";

import { AccrualModelId } from "@tsp-domains/accrual-models/types";
import { parseAccrualModelId } from "@tsp-domains/accrual-models/utils";
import { ContractTypeId } from "@tsp-domains/contract-types/types";
import { parseContractTypeId } from "@tsp-domains/contract-types/utils";
import { CurrencyId } from "@tsp-domains/money/enums";
import { StorableMoney } from "@tsp-domains/money/types";
import { transformFromStorableMoney } from "@tsp-domains/money/utils";

import { WorkSubTypeId, WorkSubTypeUuid, WorkSubTypesDictionary, WorkTypeId } from "../types";
import { parseWorkTypeId } from "../utils";

type GetWorkSubTypesResponseData = {
  workSubTypes: Array<{
    id: WorkSubTypeId;
    uuid: WorkSubTypeUuid;
    name: string;
    description: string | null;
    isAutoSignable: boolean;
    isDeprecated: boolean;
    workTypeId: WorkTypeId;
    defaults: {
      params: {
        [Key in CurrencyId]?: {
          unit_price?: StorableMoney;
          fixed_price?: StorableMoney;
        };
      };
    };
    workType: {
      contractType: {
        id: ContractTypeId;
      };
    };
    accrualModel: {
      id: AccrualModelId;
    };
  }>;
};

export const workSubTypesApi = createApi({
  reducerPath: "@work-sub-types/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  keepUnusedDataFor: 60 * 60 * 24,
  tagTypes: ["WorkSubTypesDictionary"],
  endpoints: (builder) => ({
    getWorkSubTypesDictionary: builder.query<WorkSubTypesDictionary, void>({
      query: () => ({
        url: "/workSubTypes",
        method: "GET",
      }),
      providesTags: ["WorkSubTypesDictionary"],
      transformResponse: (response: GetWorkSubTypesResponseData) => {
        return response.workSubTypes.reduce((accumulator, item) => {
          const fixedPrice = item.defaults.params?.[CurrencyId.EUR]?.fixed_price;
          const unitPrice = item.defaults.params?.[CurrencyId.EUR]?.unit_price;

          accumulator[item.uuid] = {
            uuid: item.uuid,
            id: item.id,
            name: item.name,
            description: item.description || "",
            isDeprecated: item.isDeprecated,
            isAutoSignable: item.isAutoSignable,
            contractTypeId: parseContractTypeId(item.workType.contractType.id),
            accrualModelId: parseAccrualModelId(item.accrualModel.id),
            workTypeId: parseWorkTypeId(item.workTypeId),
            fixedPrice: transformFromStorableMoney(fixedPrice),
            unitPrice: transformFromStorableMoney(unitPrice),
          };
          return accumulator;
        }, {} as WorkSubTypesDictionary);
      },
    }),
  }),
});
